import React, { Suspense } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import {
  HomeIcon,
  EnvelopeOpenIcon,
  ChatBubbleLeftRightIcon,
  UserPlusIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/solid';
import { PencilSquareIcon } from '@heroicons/react/20/solid';
import PlusCircleIcon from '@heroicons/react/24/outline/PlusCircleIcon';
import ErrorBoundaryEmpty from '../ErrorBoundaryEmpty';
import Spinner from '../Spinner';
import GDPRLinks from '../GDPRLinks';
import { IconAddressBook, IconDateAdd } from '../Icons';

const MAX_NEW_MESSAGES = 9;
const CLASS_WEBTEST_NAVBAR_MESSAGES = 'webtest-navBarMessages';

const CLASSES_ITEM =
  'h-10 flex items-center mb-2 group hover:text-blue-500 border-l-4 border-slate-100';
const CLASSES_ITEM_ACTIVE = 'text-blue-600 border-l-4 border-blue-600 font-semibold';
const CLASSES_ITEM_NO_ACTIVE = 'text-slate-800';

const CLASSES_SUBITEM =
  'h-10 pl-16 flex items-center mb-2 hover:text-blue-500 border-l-4 border-slate-100 flex-grow';
const CLASSES_SUBITEM_ACTIVE = 'text-blue-600';
const CLASSES_SUBITEM_NO_ACTIVE = 'text-slate-800';

const CLASSES_ICON = 'w-7 h-7 text-slate-500 group-hover:text-blue-500 hover:text-blue-500 icon';
const CLASSES_ICON_BOX = 'flex-none w-10 ml-2 mr-4 flex justify-center items-center';
const CLASSES_ICON_OWN = 'text-slate-500 group-hover:text-blue-500 hover:text-blue-500 icon';

const Expanded = ({
  onActionCallback,
  unreadMessages,
  chatPath,
  numScheduledCalls,
  invitationRequestsCount,
  patientsShown,
  physiciansShown,
  invitationRequestsShown,
  physiciansCount,
  patientsCount,
  staffCount,
  sessionChatCount,
  getQueryString,
  configShown,
  unreadInvitationRequestCount,
  inboxInMenuExpanded,
}) => {
  const { t } = useTranslation('components');

  return (
    <>
      <div className="flex flex-none flex-col pt-2 pr-2">
        <NavLink
          to="/"
          exact
          className={isActive =>
            `${CLASSES_ITEM} ${isActive ? CLASSES_ITEM_ACTIVE : CLASSES_ITEM_NO_ACTIVE}`
          }
          onClick={onActionCallback}
          isActive={(_, location) => location.pathname === '/'}
        >
          <div className={CLASSES_ICON_BOX}>
            <HomeIcon className={CLASSES_ICON} />
          </div>
          <div className="flex-grow truncate">{t('PhysicianNavBar.menu_items.home')}</div>
        </NavLink>

        <NavLink
          to="/inbox/requests"
          className={isActive =>
            `${CLASSES_ITEM} ${
              isActive ? CLASSES_ITEM_ACTIVE : CLASSES_ITEM_NO_ACTIVE
            } ${CLASS_WEBTEST_NAVBAR_MESSAGES} flex-auto`
          }
          isActive={(_, location) => location.pathname.startsWith('/inbox/requests')}
        >
          <div className={CLASSES_ICON_BOX}>
            <EnvelopeOpenIcon className={CLASSES_ICON} />
          </div>
          <div className="truncate">{t('PhysicianNavBar.menu_items.inbox')}</div>
          <Suspense
            fallback={
              <div className="flex w-full items-center justify-center">
                <Spinner size="3xl" />
              </div>
            }
          >
            <ErrorBoundaryEmpty>{inboxInMenuExpanded}</ErrorBoundaryEmpty>
          </Suspense>
        </NavLink>
        <div className="flex items-center">
          <NavLink
            to={chatPath || '/chat'}
            className={isActive =>
              `${CLASSES_ITEM} ${
                isActive ? CLASSES_ITEM_ACTIVE : CLASSES_ITEM_NO_ACTIVE
              } ${CLASS_WEBTEST_NAVBAR_MESSAGES} flex-grow`
            }
            isActive={(_, location) => location.pathname.includes('/chat')}
            onClick={() => {
              if (onActionCallback) onActionCallback();
            }}
          >
            <div className={CLASSES_ICON_BOX}>
              <ChatBubbleLeftRightIcon className={CLASSES_ICON} />
            </div>
            <div className="flex-grow truncate">
              {t('PhysicianNavBar.menu_items.messages', { allowHtml: true })}
            </div>
            {unreadMessages > 0 && (
              <div className="w-6 rounded bg-red-600 text-center text-sm leading-relaxed text-white">
                {unreadMessages > MAX_NEW_MESSAGES ? `${MAX_NEW_MESSAGES}+` : unreadMessages}
              </div>
            )}
          </NavLink>
          <NavLink
            className="mx-2 mb-2 flex h-10 w-10 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-300 hover:text-slate-600"
            to={getQueryString({ modal: 'newMessage' })}
            isActive={() => {}}
            onClick={() => {
              if (onActionCallback) onActionCallback();
            }}
          >
            <div className="flex h-7 w-7 flex-shrink-0 cursor-pointer items-center justify-center rounded-lg text-slate-600">
              <PencilSquareIcon className="h-7 w-7 fill-current" />
            </div>
          </NavLink>
        </div>

        {(physiciansShown || patientsShown) && (
          <NavLink
            to="/address-book"
            className={isActive =>
              `${CLASSES_ITEM} ${
                isActive ? CLASSES_ITEM_ACTIVE : CLASSES_ITEM_NO_ACTIVE
              } ${CLASS_WEBTEST_NAVBAR_MESSAGES} flex-grow`
            }
            onClick={() => {
              if (onActionCallback) onActionCallback();
            }}
          >
            <div className={CLASSES_ICON_BOX}>
              <IconAddressBook size="12" className={CLASSES_ICON_OWN} />
            </div>
            <div className="flex-grow truncate">
              {t('PhysicianNavBar.menu_items.contacts', { allowHtml: true })}
            </div>
          </NavLink>
        )}

        <div className="flex items-center">
          <NavLink
            exact
            to={`/address-book${getQueryString({ sort: 'staff' })}`}
            className={isActive =>
              `${CLASSES_SUBITEM} ${isActive ? CLASSES_SUBITEM_ACTIVE : CLASSES_SUBITEM_NO_ACTIVE}`
            }
            isActive={(match, location) => {
              if (match && location.search === getQueryString({ sort: 'staff' })) {
                return true;
              }
              return false;
            }}
            onClick={() => {
              if (onActionCallback) onActionCallback();
            }}
          >
            <div className="flex items-center truncate">
              <div className="mr-2 h-1 w-1 rounded-full bg-slate-800" />
              {t('PhysicianNavBar.menu_items.contacts_staff')}
              {Boolean(staffCount) && <span className="ml-2 text-slate-500">({staffCount})</span>}
            </div>
          </NavLink>
          {configShown && (
            <NavLink
              className="mx-2 mb-2 flex h-10 w-10 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-300 hover:text-slate-600"
              to={getQueryString({ modal: 'staff_type' })}
              isActive={() => false}
              onClick={() => {
                if (onActionCallback) onActionCallback();
              }}
            >
              <div className="flex h-8 w-8 flex-shrink-0 cursor-pointer items-center justify-center rounded-lg text-slate-600">
                <PlusCircleIcon className="w-8 h-8" />
              </div>
            </NavLink>
          )}
        </div>

        {physiciansShown && (
          <div className="flex items-center">
            <NavLink
              exact
              to={`/address-book${getQueryString({ sort: 'physicians' })}`}
              className={isActive =>
                `${CLASSES_SUBITEM} ${
                  isActive ? CLASSES_SUBITEM_ACTIVE : CLASSES_SUBITEM_NO_ACTIVE
                }`
              }
              isActive={(match, location) => {
                if (match && location.search === getQueryString({ sort: 'physicians' })) {
                  return true;
                }
                return false;
              }}
              onClick={() => {
                if (onActionCallback) onActionCallback();
              }}
            >
              <div className="flex items-center truncate">
                <div className="mr-2 h-1 w-1 rounded-full bg-slate-800" />
                {t('PhysicianNavBar.menu_items.contacts_physicians')}
                {Boolean(physiciansCount) && (
                  <span className="ml-2 text-slate-500">({physiciansCount})</span>
                )}
              </div>
            </NavLink>
            <NavLink
              className="mx-2 mb-2 flex h-10 w-10 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-300 hover:text-slate-600"
              to={getQueryString({ modal: 'invitePhysician' })}
              isActive={() => false}
              onClick={() => {
                if (onActionCallback) onActionCallback();
              }}
            >
              <div className="flex h-8 w-8 flex-shrink-0 cursor-pointer items-center justify-center rounded-lg text-slate-600">
                <PlusCircleIcon className="w-8 h-8" />
              </div>
            </NavLink>
          </div>
        )}

        {patientsShown && (
          <div className="flex items-center">
            <NavLink
              exact
              to={`/address-book${getQueryString({ sort: 'patients' })}`}
              className={isActive =>
                `${CLASSES_SUBITEM} ${
                  isActive ? CLASSES_SUBITEM_ACTIVE : CLASSES_SUBITEM_NO_ACTIVE
                }`
              }
              isActive={(match, location) => {
                if (match && location.search === getQueryString({ sort: 'patients' })) {
                  return true;
                }
                return false;
              }}
              onClick={() => {
                if (onActionCallback) onActionCallback();
              }}
            >
              <div className="flex items-center truncate">
                <div className="mr-2 h-1 w-1 rounded-full bg-slate-800" />
                {t('PhysicianNavBar.menu_items.paired_patients')}
                {Boolean(patientsCount) && (
                  <span className="ml-2 text-slate-500">({patientsCount})</span>
                )}
              </div>
            </NavLink>
            <NavLink
              className="mx-2 mb-2 flex h-10 w-10 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-300 hover:text-slate-600"
              to={getQueryString({ invitePatient: 'email' })}
              isActive={() => false}
              onClick={() => {
                if (onActionCallback) onActionCallback();
              }}
            >
              <div className="flex h-8 w-8 flex-shrink-0 cursor-pointer items-center justify-center rounded-lg text-slate-600">
                <PlusCircleIcon className="w-8 h-8" />
              </div>
            </NavLink>
          </div>
        )}

        <div className="flex items-center">
          <NavLink
            exact
            to={`/address-book${getQueryString({ sort: 'groups' })}`}
            className={isActive =>
              `${CLASSES_SUBITEM} ${isActive ? CLASSES_SUBITEM_ACTIVE : CLASSES_SUBITEM_NO_ACTIVE}`
            }
            isActive={(match, location) => {
              if (match && location.search === getQueryString({ sort: 'groups' })) {
                return true;
              }
              return false;
            }}
            onClick={() => {
              if (onActionCallback) onActionCallback();
            }}
          >
            <div className="flex items-center truncate">
              <div className="mr-2 h-1 w-1 rounded-full bg-slate-800" />
              {t('PhysicianNavBar.menu_items.contacts_session_chats')}
              {Boolean(sessionChatCount) && (
                <span className="ml-2 text-slate-500">({sessionChatCount})</span>
              )}
            </div>
          </NavLink>
          <NavLink
            className="mx-2 mb-2 flex h-10 w-10 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-300 hover:text-slate-600"
            to={getQueryString({ modal: 'sessionChatCreate' })}
            isActive={() => false}
            onClick={() => {
              if (onActionCallback) onActionCallback();
            }}
          >
            <div className="flex h-8 w-8 flex-shrink-0 cursor-pointer items-center justify-center rounded-lg text-slate-600">
              <PlusCircleIcon className="w-8 h-8" />
            </div>
          </NavLink>
        </div>

        {invitationRequestsShown && (
          <NavLink
            exact
            to="/invitation-requests"
            className={isActive =>
              `${CLASSES_ITEM} ${isActive ? CLASSES_ITEM_ACTIVE : CLASSES_ITEM_NO_ACTIVE}`
            }
            onClick={() => {
              if (onActionCallback) onActionCallback();
            }}
          >
            <div className={CLASSES_ICON_BOX}>
              <UserPlusIcon className={CLASSES_ICON} />
            </div>
            <div className="flex-grow truncate">
              <span className="truncate">
                {t('PhysicianNavBar.menu_items.invitation_requests')}
              </span>
              {invitationRequestsCount ? (
                <span className="ml-2 text-slate-500">({invitationRequestsCount})</span>
              ) : null}
            </div>
            {unreadInvitationRequestCount > 0 && (
              <div className="mx-4 w-6 rounded bg-red-600 text-center text-sm leading-relaxed text-white">
                {unreadInvitationRequestCount > MAX_NEW_MESSAGES
                  ? `${MAX_NEW_MESSAGES}+`
                  : unreadInvitationRequestCount}
              </div>
            )}
          </NavLink>
        )}

        <div className="flex items-center">
          <NavLink
            to="/sessions"
            className={isActive =>
              `${CLASSES_ITEM} ${
                isActive ? CLASSES_ITEM_ACTIVE : CLASSES_ITEM_NO_ACTIVE
              } ${CLASS_WEBTEST_NAVBAR_MESSAGES} flex-auto`
            }
            onClick={() => {
              if (onActionCallback) onActionCallback();
            }}
          >
            <div className={CLASSES_ICON_BOX}>
              <VideoCameraIcon className={CLASSES_ICON} />
            </div>
            <div className="flex-grow truncate">
              {t('PhysicianNavBar.menu_items.video_lists')}
              {numScheduledCalls ? (
                <span className="ml-2 font-normal text-slate-500">({numScheduledCalls})</span>
              ) : (
                ''
              )}
            </div>
          </NavLink>

          <NavLink
            className="mx-2 mb-2 flex h-10 w-10 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-300 hover:text-slate-600"
            to={getQueryString({ modal: 'videoauswahl' })}
            isActive={() => {}}
            onClick={() => {
              if (onActionCallback) onActionCallback();
            }}
          >
            <div className="flex h-7 w-7 flex-shrink-0 cursor-pointer items-center justify-center rounded-lg text-slate-600">
              <IconDateAdd size="8" />
            </div>
          </NavLink>
        </div>

        <hr className="my-2 ml-6 mr-12 border-t-1" />

        <NavLink
          to="/settings/contact"
          className={isActive =>
            `${CLASSES_SUBITEM} ${isActive ? CLASSES_SUBITEM_ACTIVE : CLASSES_SUBITEM_NO_ACTIVE}`
          }
          isActive={(_, location) => location.pathname.includes('contact')}
          onClick={() => {
            if (onActionCallback) onActionCallback();
          }}
        >
          <div className="flex-grow truncate">
            {t('PhysicianNavBar.menu_items.physician_pages')}
          </div>
        </NavLink>

        <NavLink
          to="/settings/infoMaterialForPatients"
          className={isActive =>
            `${CLASSES_SUBITEM} ${isActive ? CLASSES_SUBITEM_ACTIVE : CLASSES_SUBITEM_NO_ACTIVE}`
          }
          isActive={(_, location) => location.pathname.includes('infoMaterialForPatients')}
          onClick={() => {
            if (onActionCallback) onActionCallback();
          }}
        >
          <div className="flex-grow truncate">{t('PhysicianNavBar.menu_items.info_materials')}</div>
        </NavLink>

        <NavLink
          to="/settings/status"
          className={isActive =>
            `${CLASSES_SUBITEM} ${isActive ? CLASSES_SUBITEM_ACTIVE : CLASSES_SUBITEM_NO_ACTIVE}`
          }
          isActive={(_, location) => location.pathname.includes('status')}
          onClick={() => {
            if (onActionCallback) onActionCallback();
          }}
        >
          <div className="flex-grow truncate">{t('PhysicianNavBar.menu_items.edit_status')}</div>
        </NavLink>

        <NavLink
          to="/settings/snippets"
          className={isActive =>
            `${CLASSES_SUBITEM} ${isActive ? CLASSES_SUBITEM_ACTIVE : CLASSES_SUBITEM_NO_ACTIVE}`
          }
          isActive={(_, location) => location.pathname.includes('snippets')}
          onClick={() => {
            if (onActionCallback) onActionCallback();
          }}
        >
          <div className="flex-grow truncate">{t('PhysicianNavBar.menu_items.text_snippets')}</div>
        </NavLink>
      </div>
      <div className="flex-grow" />
      <GDPRLinks />
    </>
  );
};

Expanded.propTypes = {
  onActionCallback: PropTypes.func,
  unreadMessages: PropTypes.number,
  chatPath: PropTypes.string,
  numScheduledCalls: PropTypes.number,
  invitationRequestsCount: PropTypes.number,
  patientsShown: PropTypes.bool,
  physiciansShown: PropTypes.bool,
  invitationRequestsShown: PropTypes.bool,
  physiciansCount: PropTypes.number,
  patientsCount: PropTypes.number,
  staffCount: PropTypes.number,
  sessionChatCount: PropTypes.number,
  getQueryString: PropTypes.func,
  configShown: PropTypes.bool,
  unreadInvitationRequestCount: PropTypes.number,
  inboxInMenuExpanded: PropTypes.node,
};

export default Expanded;
